import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useServices } from "@/hooks/useServices";
import { useContractTypes } from "@/hooks/useContractTypes";
import { useCurrencies } from "@/hooks/useCurrencies";
import { Service, ServiceType } from "@/lib/types/services";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const serviceFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  service_type: z.enum(["product", "service", "subscription", "license", "other"]),
  cost: z.coerce.number().min(0, "Cost must be a positive number"),
  currency_id: z.string().min(1, "Currency is required"),
  contract_type_ids: z.array(z.string()).optional(),
});

type ServiceFormValues = z.infer<typeof serviceFormSchema>;

interface ServiceFormProps {
  service?: Service;
  onSuccess?: (service: Service) => void;
  onCancel?: () => void;
  contextType?: "contract-edit" | "standalone";
  contractId?: string;
  preventNavigation?: boolean;
}

export function ServiceForm({ 
  service, 
  onSuccess, 
  onCancel, 
  contextType = "standalone",
  contractId,
  preventNavigation = false
}: ServiceFormProps) {
  const { 
    useServicesQuery, 
    useCreateServiceMutation, 
    useUpdateServiceMutation, 
    useLinkServiceToContractTypesMutation,
    useAddServiceToContractMutation
  } = useServices();
  
  const createServiceMutation = useCreateServiceMutation();
  const updateServiceMutation = useUpdateServiceMutation();
  const linkContractTypesMutation = useLinkServiceToContractTypesMutation();
  const addServiceToContractMutation = useAddServiceToContractMutation();

  const { useContractTypesQuery } = useContractTypes();
  const { data: contractTypes = [] } = useContractTypesQuery();

  const { useCurrenciesQuery } = useCurrencies();
  const { data: currencies = [] } = useCurrenciesQuery();

  const [selectedContractTypes, setSelectedContractTypes] = useState<string[]>([]);

  const form = useForm<ServiceFormValues>({
    resolver: zodResolver(serviceFormSchema),
    defaultValues: service
      ? {
          name: service.name,
          description: service.description || "",
          service_type: service.service_type as ServiceType,
          cost: service.cost,
          currency_id: service.currency_id || "",
          contract_type_ids: [],
        }
      : {
          name: "",
          description: "",
          service_type: "service" as ServiceType,
          cost: 0,
          currency_id: currencies.length > 0 ? currencies[0].id : "",
          contract_type_ids: [],
        },
  });

  useEffect(() => {
    if (service && service.id) {
      setSelectedContractTypes([]);
    }
  }, [service]);

  const onSubmit = async (values: ServiceFormValues) => {
    try {
      let resultService;
      
      if (service) {
        const updatedService = {
          id: service.id,
          name: values.name,
          description: values.description,
          service_type: values.service_type,
          cost: values.cost,
          currency_id: values.currency_id
        };

        resultService = await updateServiceMutation.mutateAsync(updatedService);

        await linkContractTypesMutation.mutateAsync({
          serviceId: resultService.id,
          contractTypeIds: selectedContractTypes
        });
        
        toast.success("Service updated successfully");
      } else {
        const newService = {
          name: values.name,
          description: values.description,
          service_type: values.service_type,
          cost: values.cost,
          currency_id: values.currency_id
        };

        resultService = await createServiceMutation.mutateAsync(newService);

        await linkContractTypesMutation.mutateAsync({
          serviceId: resultService.id,
          contractTypeIds: selectedContractTypes
        });
        
        toast.success("Service created successfully");
      }
      
      if (contextType === "contract-edit" && contractId && resultService) {
        await addServiceToContractMutation.mutateAsync({
          contractId,
          serviceId: resultService.id,
          quantity: 1
        });
        toast.success("Service added to contract");
      }

      if (onSuccess) {
        onSuccess(resultService);
      }
    } catch (error) {
      console.error("Error saving service:", error);
      toast.error("Failed to save service");
    }
  };

  const handleContractTypeToggle = (contractTypeId: string) => {
    setSelectedContractTypes(prev => {
      if (prev.includes(contractTypeId)) {
        return prev.filter(id => id !== contractTypeId);
      } else {
        return [...prev, contractTypeId];
      }
    });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.stopPropagation();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" onClick={handleFormSubmit}>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Service Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter service name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Enter service description" 
                  className="min-h-[100px]"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="service_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Type</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select service type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="product">Product</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="subscription">Subscription</SelectItem>
                    <SelectItem value="license">License</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="cost"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cost</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      step="0.01" 
                      min="0"
                      placeholder="0.00" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="currency_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Currency</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {currencies.map((currency) => (
                        <SelectItem key={currency.id} value={currency.id}>
                          {currency.code} ({currency.symbol})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div>
          <h3 className="mb-3 font-medium">Compatible Contract Types</h3>
          <div className="space-y-2">
            {contractTypes.length > 0 ? (
              contractTypes.map((contractType) => (
                <div key={contractType.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`contract-type-${contractType.id}`}
                    checked={selectedContractTypes.includes(contractType.id)}
                    onCheckedChange={() => handleContractTypeToggle(contractType.id)}
                  />
                  <label
                    htmlFor={`contract-type-${contractType.id}`}
                    className="text-sm cursor-pointer"
                  >
                    {contractType.name}
                  </label>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No contract types available</p>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button 
            type="submit" 
            disabled={createServiceMutation.isPending || updateServiceMutation.isPending}
          >
            {service ? "Update Service" : "Create Service"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
